import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AnyAbility } from '@casl/ability';
import { AblePurePipe } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faInfoCircle, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState, UpdateOrgUnitName } from '@wilson/auth/core';
import { AuthorizationModule } from '@wilson/authorization';
import {
  BankDetailsComponent,
  BankDetailsGatewayService,
  CreateBankDetails,
  DeleteBankDetail,
  FetchBankDetailsIfNeeded,
  UpdateBankDetails,
} from '@wilson/bank-details';
import {
  CreateGeneralGeneralCompanySettings,
  ForceFetchGeneralCompanySettings,
  GeneralCompanySettingsGatewayService,
  UpdateGeneralGeneralCompanySettings,
} from '@wilson/general-company-settings';
import {
  BankDetails,
  BankDetailsForm,
  GeneralCompanySettings,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import { OrganizationalUnitService } from '@wilson/organizational-unit';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { from, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { GeneralSettingsAddressComponent } from './general-settings-address/general-settings-address.component';
import { GeneralSettingsDetailsComponent } from './general-settings-details/general-settings-details.component';
import { GeneralSettingsHelperService } from './general-settings-helper.service';
import { GeneralSettingsTaxComponent } from './general-settings-tax/general-settings-tax.component';
@Component({
  selector: 'wilson-general-settings',
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzFormModule,
    NzInputModule,
    NzUploadModule,
    NzModalModule,
    NzTooltipModule,
    NzIconModule,
    GeneralSettingsAddressComponent,
    GeneralSettingsTaxComponent,
    BankDetailsComponent,
    FontAwesomeModule,
    GeneralSettingsDetailsComponent,
    AuthorizationModule,
  ],
  providers: [GeneralSettingsHelperService],
})
export class GeneralSettingsComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private organizationalUnitService = inject(OrganizationalUnitService);
  private messageService = inject(NzMessageService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private bankDetailsGatewayService = inject(BankDetailsGatewayService);
  private generalCompanySettingsGatewayService = inject(
    GeneralCompanySettingsGatewayService,
  );
  private helperService = inject(GeneralSettingsHelperService);
  private ablePurePipe = inject<AblePurePipe<AnyAbility>>(AblePurePipe);

  generalCompanySettings$: Observable<GeneralCompanySettings> =
    this.helperService.getGeneralCompanySettings$();
  bankDetails$: Observable<BankDetails[]> =
    this.helperService.getBankDetails$();

  companyName = '';
  loading = false;

  companyLogoUrl: string | ArrayBuffer | null = null;
  companyLogoFileSizeStatus: 'error' | undefined;
  private readonly companyLogoFileMaxSizeInBytes = 2e6;

  faTrash = faTrash;

  readonly rootOrgUnit$ = this.store.select(AuthState.rootOrgUnit);
  private readonly destroy$ = new Subject<void>();

  generalSettingsForm: FormGroup =
    this.helperService.createGeneralSettingsForm();

  get infoDetailsForm(): FormGroup {
    return this.generalSettingsForm.get('infoDetails') as FormGroup;
  }

  get addressDetailsForm(): FormGroup {
    return this.generalSettingsForm.get('addressDetails') as FormGroup;
  }

  get vatDetailsForm(): FormGroup {
    return this.generalSettingsForm.get('vatDetails') as FormGroup;
  }

  bankDetailsFormArray!: FormArray<FormGroup<BankDetailsForm>>;

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected hasGeneralSettingsUpdatePermission$: Observable<boolean> =
    of(false);

  async ngOnInit(): Promise<void> {
    this.hasGeneralSettingsUpdatePermission$ = this.ablePurePipe
      .transform(RoleAction.Update, RolePermissionSubject.GeneralSetting)
      .pipe(
        tap((hasPermission: boolean) => {
          this.setFormValues(hasPermission);
        }),
      );
    this.store.dispatch(new FetchBankDetailsIfNeeded());
  }

  addMoreBankDetails(): void {
    this.bankDetailsFormArray.push(this.createBankDetailsFormGroup());
  }

  beforeUpload = (file: NzUploadFile): Observable<boolean> => {
    this.companyLogoFileSizeStatus =
      (file as unknown as File).size > this.companyLogoFileMaxSizeInBytes
        ? 'error'
        : undefined;

    return of(this.companyLogoFileSizeStatus !== 'error').pipe(
      tap(async (shouldSetLogo) => {
        if (shouldSetLogo) {
          // problem with ng-zorro nzBeforeUpload type https://github.com/NG-ZORRO/ng-zorro-antd/issues/4744
          await this.setCompanyLogo(file as unknown as File);
        }
      }),
    );
  };

  createBankDetailsFormGroup(bankDetail?: BankDetails): FormGroup {
    return new FormGroup({
      id: new FormControl(bankDetail?.id || null),
      bankName: new FormControl(bankDetail?.bankName || null, [
        Validators.required,
      ]),
      iban: new FormControl(bankDetail?.iban || null, [Validators.required]),
      bic: new FormControl(bankDetail?.bic || null, [Validators.required]),
      bankCode: new FormControl(bankDetail?.bankCode || null),
      accountNumber: new FormControl(bankDetail?.accountNumber || null),
      accountHolderName: new FormControl(bankDetail?.accountHolderName || null),
      isDefault: new FormControl(bankDetail?.isDefault || false),
    });
  }

  async onCompanyNameChange(): Promise<void> {
    const rootOrgUnit = this.store.selectSnapshot(AuthState.rootOrgUnit);
    if (rootOrgUnit) {
      if (this.companyName === '') {
        this.companyName = rootOrgUnit.name as string;
      }

      const oldCompanyName = rootOrgUnit.name as string;

      if (!this.companyName || this.companyName.trim() === '') {
        this.messageService.error(
          this.translate.instant('page.general_settings.company_name_required'),
        );
        this.store.dispatch(new UpdateOrgUnitName(oldCompanyName));
        return;
      }

      const { mobileSettings, ...restOrgUnit } = rootOrgUnit;

      const updatedOrgUnit = {
        ...restOrgUnit,
        name: this.companyName,
      };

      this.store.dispatch(new UpdateOrgUnitName(this.companyName));
      try {
        await this.organizationalUnitService.updateOrgUnit(
          updatedOrgUnit,
          updatedOrgUnit.id as string,
        );
      } catch {
        this.store.dispatch(new UpdateOrgUnitName(oldCompanyName));
      }
    }
  }

  async addBankDetails(bankDetails: BankDetails): Promise<void> {
    const areAllBankDetailsValid = this.bankDetailsFormArray.controls.every(
      (control) => control.valid,
    );

    if (this.bankDetailsFormArray.value.length === 1) {
      bankDetails.isDefault = true;
    }

    if (!areAllBankDetailsValid) {
      return;
    }

    const transformedBankDetails =
      this.helperService.transformEmptyStringsToNull(
        bankDetails,
      ) as BankDetails;

    if (transformedBankDetails.id === null) {
      const newBankDetails =
        await this.bankDetailsGatewayService.createBankDetails(
          transformedBankDetails,
        );

      this.store.dispatch(new CreateBankDetails(newBankDetails));
    } else {
      this.store.dispatch(new UpdateBankDetails(transformedBankDetails));
    }
    this.cdr.markForCheck();
  }

  deleteDetails(bankDetailsId: string): void {
    this.store.dispatch(new DeleteBankDetail(bankDetailsId));
  }

  async addOrUpdateGeneralSettings(): Promise<void> {
    this.helperService.setEmptyValuesToNull(this.generalSettingsForm);

    const { infoDetails } = this.generalSettingsForm.value;

    const emailErrors =
      this.generalSettingsForm.get('infoDetails.email')?.errors;

    if (emailErrors !== null) {
      return;
    }

    const generalSettings = {
      ...infoDetails,
    } as GeneralCompanySettings;

    this.createOrUpdateGeneralSettings(generalSettings);
  }

  async addOrUpdateVatDetails(): Promise<void> {
    this.helperService.setEmptyValuesToNull(this.generalSettingsForm);

    const { vatDetails } = this.generalSettingsForm.value;

    const generalSettings = {
      ...vatDetails,
    } as GeneralCompanySettings;

    this.createOrUpdateGeneralSettings(generalSettings);
  }

  async addOrUpdateAddressSettings(): Promise<void> {
    this.helperService.setEmptyValuesToNull(this.generalSettingsForm);

    const { addressDetails } = this.generalSettingsForm.value;

    const isEmptyAddress =
      addressDetails &&
      Object.values(addressDetails).every((field) => field === null);

    if (isEmptyAddress) {
      const generalSettings = {
        addressId: null,
        address: null,
      } as GeneralCompanySettings;

      this.store.dispatch(
        new UpdateGeneralGeneralCompanySettings(generalSettings),
      );

      return;
    }

    if (this.generalSettingsForm.controls['addressDetails'].invalid) {
      return;
    }

    const generalSettings = {
      address: addressDetails,
    } as GeneralCompanySettings;

    this.createOrUpdateGeneralSettings(generalSettings);
  }

  async createOrUpdateGeneralSettings(
    generalSettings: GeneralCompanySettings,
  ): Promise<void> {
    if (this.generalSettingsForm.value.id) {
      this.store.dispatch(
        new UpdateGeneralGeneralCompanySettings(generalSettings),
      );
    } else {
      this.store.dispatch(
        new CreateGeneralGeneralCompanySettings(generalSettings),
      );
    }
  }

  async setCompanyLogo(companyLogo: File): Promise<void> {
    try {
      await this.generalCompanySettingsGatewayService.addCompanyLogo(
        this.generalSettingsForm.value.id,
        companyLogo,
      );
      this.store.dispatch(new ForceFetchGeneralCompanySettings());
    } catch {
      this.messageService.error(this.translate.instant('general.error'));
    }
  }

  async removeCompanyLogo(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    try {
      await this.generalCompanySettingsGatewayService.updateGeneralSettings(
        this.generalSettingsForm.value.id,
        { companyLogo: null },
      );
      this.companyLogoUrl = null;
      this.generalSettingsForm.patchValue({ companyLogo: null });
      this.cdr.markForCheck();
    } catch {
      this.messageService.error(this.translate.instant('general.error'));
    }
  }

  private setFormValues(hasPermissions: boolean): void {
    this.toggleFormBasedOnPermissions(hasPermissions);
    this.getCompanyName();
    this.patchGeneralCompanySettings();
    this.patchBankDetails(hasPermissions);
  }

  private toggleFormBasedOnPermissions(hasPermissions: boolean): void {
    if (hasPermissions) {
      this.generalSettingsForm.enable();
    } else {
      this.generalSettingsForm.disable();
    }
  }

  private getCompanyName(): void {
    this.rootOrgUnit$
      .pipe(takeUntil(this.destroy$))
      .subscribe((rootOrgUnit) => {
        this.companyName = rootOrgUnit?.name || '';
      });
  }

  private patchBankDetails(hasPermissions: boolean): void {
    this.bankDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((bankDetails) => {
        this.bankDetailsFormArray =
          this.helperService.createBankDetailsFormArray(
            bankDetails,
            hasPermissions,
          );
        this.cdr.markForCheck();
      });
  }

  private patchGeneralCompanySettings(): void {
    this.generalCompanySettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.companyLogoUrl = settings.companyLogo as string;
        this.generalSettingsForm.patchValue({
          id: settings?.id,
          addressDetails: settings.address,
          infoDetails: {
            websiteUrl: settings.websiteUrl,
            phone: settings.phone,
            email: settings.email,
          },
          vatDetails: {
            vatId: settings?.vatId,
            taxIdentificationNumber: settings?.taxIdentificationNumber,
            generalManager: settings?.generalManager,
            localCourt: settings?.localCourt,
            tradeRegisterNumber: settings?.tradeRegisterNumber,
          },
        });
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
